import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { DialogContent } from '@mui/material'
import HHDialogActions from 'components/common/HHDialogActions'
import { HHFormPriceField } from 'components/form-fields/v5'
import { get, noop } from 'lodash'
import { useForm } from 'react-hook-form'
import { formatCentsToDollars, formatDollarsToCents } from 'utils/price'
import SaveButton from 'components/buttons/SaveButton'
import CancelButton from 'components/buttons/CancelButton'
import T from 'T'
import { useUpdateCreditLimitMutation, useLazyGetCreditLimitHistoryByAccountQuery } from 'api/accounts/getAccountDetails'
import { getCustomerDetails } from 'middleware/actions/customers'
import { useDispatch } from 'react-redux'
import { handleError } from 'utils/error'
import SettingsHistoryAccordion from './common/SettingsHistoryAccordion'

const CreditLimitEditDialog = ({ open, onClose = noop, customerDetails }) => {
  const creditLimitCents = get(customerDetails, 'creditLimitCents', 0)
  const [updateCreditLimit, { isLoading }] = useUpdateCreditLimitMutation()
  const [
    getCreditLimitHistory,
    { data: historyData, isLoading: isHistoryLoading, isFetching: isHistoryFetching, isSuccess: isHistorySuccess },
  ] = useLazyGetCreditLimitHistoryByAccountQuery()

  const accountId = get(customerDetails, 'id')
  const dispatch = useDispatch()
  const { control, reset, handleSubmit } = useForm({
    defaultValues: {
      creditLimit: formatCentsToDollars(creditLimitCents),
    },
  })

  const versions = useMemo(() => get(historyData, 'history', []), [historyData])
  const hasVersions = useMemo(() => versions.length > 0 && isHistorySuccess, [versions, isHistorySuccess])
  const versionsLoading = useMemo(() => isHistoryLoading || isHistoryFetching, [isHistoryLoading, isHistoryFetching])

  const getVersionChangeLines = useCallback(version => {
    const { creditLimitCents: limitCents } = version
    const dollars = formatCentsToDollars(limitCents)
    return [`Changed to $${dollars}`]
  }, [])

  const handleSave = useCallback(
    data => {
      const creditLimit = get(data, 'creditLimit', 0)
      updateCreditLimit({
        creditLimitCents: formatDollarsToCents(creditLimit),
        accountId,
      })
        .unwrap()
        .then(() => {
          onClose()
          dispatch(getCustomerDetails({ accountId }))
        })
        .catch(handleError)
    },
    [accountId, updateCreditLimit, onClose, dispatch]
  )

  useEffect(() => {
    if (open && accountId) {
      reset({
        creditLimit: formatCentsToDollars(creditLimitCents),
        accountId,
      })
      getCreditLimitHistory({ accountId }).unwrap().catch(handleError)
    }
  }, [open, customerDetails, accountId, creditLimitCents, reset, getCreditLimitHistory])

  return (
    <HHBaseDialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
      <HHDialogTitle onClose={onClose} title={T.CREDIT_LIMIT} />
      <DialogContent>
        <HHFormPriceField
          sx={{
            mt: 0.8,
            mb: 2,
          }}
          name="creditLimit"
          control={control}
          fullWidth
        />
        <SettingsHistoryAccordion
          versions={versions}
          versionsLoading={versionsLoading}
          hasVersions={hasVersions}
          getVersionChangeLines={getVersionChangeLines}
        />
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton disabled={isLoading} loading={isLoading} onClick={handleSubmit(handleSave)} />
      </HHDialogActions>
    </HHBaseDialog>
  )
}

CreditLimitEditDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  customerDetails: PropTypes.object,
}

export default CreditLimitEditDialog
